import { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Loader } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import useLocationCarrierApi, { LocationCarrier } from 'hooks/use-location-carrier-api';
import { formatTruckingCompany } from 'utils/format';

import LockableDateInput from 'components/inputs/date/lockable';
import dayjs from 'dayjs';
import './rule.scss';

const EditLocationRule: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { editRule, getRule } = useLocationCarrierApi();

  const [loading, setLoading] = useState<boolean>(false);

  const [rule, setRule] = useState<LocationCarrier | null>();

  useEffect(() => {
    if (id) {
      setLoading(true);
      getRule(id)
        .then(setRule)
        .finally(() => setLoading(false));
    }
  }, [id, getRule, setRule]);

  const handleReset = useCallback(() => navigate('/scac-management/location-rules', { replace: true }), [navigate]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLoading(true);
      const response = await editRule(rule);
      if (response) {
        navigate('/scac-management/location-rules', { replace: true });
      }
      setLoading(false);
    },
    [rule, editRule, navigate, setLoading]
  );

  return (
    <Loader loading={loading}>
      <ErrorDialog />

      <div id="scac-management" className="page">
        <div className="page-header">
          <h1>SCAC MANAGEMENT</h1>
        </div>
        <div className="page-content h-100">
          <form
            id="location-carrier-rule"
            className="card-primary"
            onReset={handleReset}
            onSubmit={handleSubmit}
            data-cy="location-rule-form"
          >
            <div className="card-header mb-4">
              <h4>EDIT LOCATION RULE</h4>
              <div className="grid-2">
                <button className="btn-outline" type="reset">
                  CANCEL
                </button>
                <button className="btn-primary" type="submit">
                  SUBMIT
                </button>
              </div>
            </div>
            <div className="card-body grid-auto">
              <div className="text-field">
                <input
                  className="h-100 w-100"
                  id="id-input"
                  name="id"
                  defaultValue={rule?.id ?? ''}
                  readOnly
                  disabled
                />
                <label htmlFor="id-input">ID</label>
              </div>
              <div className="text-field">
                <input
                  className="h-100 w-100"
                  id="location-input"
                  name="location"
                  defaultValue={rule?.location?.name ?? ''}
                  readOnly
                  disabled
                />
                <label htmlFor="location-input">LOCATION</label>
              </div>
              <div className="text-field">
                <input
                  className="h-100 w-100"
                  id="carrier-input"
                  name="carrier"
                  defaultValue={formatTruckingCompany(rule?.truckingCompany)}
                  readOnly
                  disabled
                />
                <label htmlFor="carrier-input">CARRIER</label>
              </div>
              <LockableDateInput
                date={rule?.startDate}
                setDate={startDate => setRule(prevState => prevState && { ...prevState, startDate })}
                label="START DATE"
                min={dayjs().toISOString()}
                data-cy="start-date-input"
              />
              <div className="text-field col-span-2">
                <input
                  className={`h-100 w-100 ${!rule?.notes ? 'blank' : ''}`}
                  id="notes-input"
                  name="notes"
                  value={rule?.notes ?? ''}
                  onChange={e => setRule(prevState => prevState && { ...prevState, notes: e.target.value })}
                  data-cy="notes-input"
                />
                <label htmlFor="notes-input">NOTES</label>
              </div>
              <div className="grid-2 mt-2">
                <div className="text-field">
                  <input
                    id="gate-in-checkbox"
                    type="checkbox"
                    checked={!!rule?.gateInAllowed}
                    onChange={e => setRule(prevState => prevState && { ...prevState, gateInAllowed: e.target.checked })}
                    data-cy="allow-gate-in-checkbox"
                  />
                  <label htmlFor="gate-in-checkbox">ALLOW GATE IN</label>
                </div>
                <div className="text-field">
                  <input
                    id="gate-out-checkbox"
                    type="checkbox"
                    checked={!!rule?.gateOutAllowed}
                    onChange={e =>
                      setRule(prevState => prevState && { ...prevState, gateOutAllowed: e.target.checked })
                    }
                    data-cy="allow-gate-out-checkbox"
                  />
                  <label htmlFor="gate-out-checkbox">ALLOW GATE OUT</label>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Loader>
  );
};

export default EditLocationRule;
